import { useTranslation } from 'react-i18next';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';


export type RecurrenceType = 'weekly' | 'biweekly' | 'monthly';

const WEEK_OPTIONS_KEYS = [
  { value: 1, key: 'first' },
  { value: 2, key: 'second' },
  { value: 3, key: 'third' },
  { value: 4, key: 'fourth' },
  { value: -1, key: 'last' },
] as const;

const DAY_FULL_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;


interface RecurrenceSelectorProps {
  recurrenceType: RecurrenceType;
  onRecurrenceTypeChange: (type: RecurrenceType) => void;
  recurrenceInterval: number;
  onRecurrenceIntervalChange: (interval: number) => void;
  monthlyWeek: number | null;
  onMonthlyWeekChange: (week: number) => void;
  monthlyDay: number | null;
  onMonthlyDayChange: (day: number) => void;
  disabled?: boolean;
}

export function RecurrenceSelector({
  recurrenceType,
  onRecurrenceTypeChange,
  recurrenceInterval,
  onRecurrenceIntervalChange,
  monthlyWeek,
  onMonthlyWeekChange,
  monthlyDay,
  onMonthlyDayChange,
  disabled = false,
}: RecurrenceSelectorProps) {
  const { t } = useTranslation('staff');

  const getDayFullName = (dayIndex: number) => t(`availability.days.${DAY_FULL_KEYS[dayIndex]}`);

  return (
    <div className="space-y-4">
      {/* Recurrence type selector */}
      <div className="space-y-2">
        <Select
          value={recurrenceType}
          onValueChange={(v) => onRecurrenceTypeChange(v as RecurrenceType)}
          disabled={disabled}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder={t('availability.recurrence.selectRecurrence')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="weekly">{t('availability.recurrence.weekly')}</SelectItem>
            <SelectItem value="biweekly">{t('availability.recurrence.biweekly')}</SelectItem>
            <SelectItem value="monthly">{t('availability.recurrence.monthly')}</SelectItem>
          </SelectContent>
        </Select>
      </div>



      {/* Monthly pattern */}
      {recurrenceType === 'monthly' && (
        <div className="space-y-3">
          <span className="text-sm text-gray-600">{t('availability.recurrence.onThe')}</span>
          <div className="grid grid-cols-2 gap-2">
            <Select
              value={monthlyWeek?.toString() || '1'}
              onValueChange={(v) => onMonthlyWeekChange(parseInt(v, 10))}
              disabled={disabled}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('availability.recurrence.weekOptions.first')} />
              </SelectTrigger>
              <SelectContent>
                {WEEK_OPTIONS_KEYS.map((option) => (
                  <SelectItem key={option.value} value={option.value.toString()}>
                    {t(`availability.recurrence.weekOptions.${option.key}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={monthlyDay?.toString() || '1'}
              onValueChange={(v) => onMonthlyDayChange(parseInt(v, 10))}
              disabled={disabled}
            >
              <SelectTrigger>
                <SelectValue placeholder={getDayFullName(1)} />
              </SelectTrigger>
              <SelectContent>
                {[0, 1, 2, 3, 4, 5, 6].map((dayIndex) => (
                  <SelectItem key={dayIndex} value={dayIndex.toString()}>
                    {getDayFullName(dayIndex)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <span className="text-sm text-gray-500">{t('availability.recurrence.ofEveryMonth')}</span>
        </div>
      )}
    </div>
  );
}
